const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const usdCents = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
const intFmt = new Intl.NumberFormat('en-US');

/** $12,500 from cents. Pass exact=true for cents precision. */
export function money(cents: number | null | undefined, exact = false): string {
  if (cents === null || cents === undefined) return '—';
  return (exact ? usdCents : usd).format(cents / 100);
}

/** Compact money for cards: $1.2M, $85K. */
export function moneyShort(cents: number | null | undefined): string {
  if (!cents) return '$0';
  const d = cents / 100;
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(d >= 10_000_000 ? 0 : 1)}M`;
  if (d >= 1_000) return `$${Math.round(d / 1_000)}K`;
  return usd.format(d);
}

export const num = (n: number | null | undefined) => (n === null || n === undefined ? '—' : intFmt.format(n));
export const sqft = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${intFmt.format(Math.round(n))} SF`);

export function date(value: string | Date | null | undefined, opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }) {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', opts);
}

export function dateTime(value: string | Date | null | undefined) {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "3 days ago", "in 5 days", "today". */
export function relative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  const diff = d.getTime() - Date.now();
  const days = Math.round(diff / 864e5);
  if (Math.abs(diff) < 864e5 && new Date().toDateString() === d.toDateString()) {
    if (Math.abs(diff) < 90_000) return 'just now';
    const mins = Math.round(diff / 60_000);
    if (Math.abs(mins) < 60) return mins > 0 ? `in ${mins} min` : `${-mins} min ago`;
    const hrs = Math.round(mins / 60);
    return hrs >= 0 ? `in ${hrs} h` : `${-hrs} h ago`;
  }
  if (days === 0) return diff > 0 ? 'tomorrow' : 'yesterday';
  if (days > 0) return days === 1 ? 'tomorrow' : `in ${days} days`;
  return days === -1 ? 'yesterday' : `${-days} days ago`;
}

/** yyyy-mm-dd for <input type="date">. */
export function toDateInput(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  return d.toISOString().slice(0, 10);
}

export function initials(name: string | null | undefined) {
  return (name ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

export function bytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Dollars typed by a user ("12,500") to integer cents. */
export function dollarsToCents(value: string): number | null {
  const n = Number(value.replace(/[$,\s]/g, ''));
  return value.trim() === '' || Number.isNaN(n) ? null : Math.round(n * 100);
}
